import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTranslation } from '../../utils/translationHelpers';
import { userAuth } from '../../services/userAuth';
import { RegistrationModal } from '../auth/RegistrationModal';
import './PostTestFeedback.css';

interface PostTestFeedbackProps {
  isOpen: boolean;
  onClose: () => void;
  testNumber: 2 | 3;
  correct: number;
  total: number;
}

export const PostTestFeedback: React.FC<PostTestFeedbackProps> = ({
  isOpen,
  onClose,
  testNumber,
  correct,
  total,
}) => {
  const navigate = useNavigate();
  const { t_nested } = useLanguage();
  const [showRegModal, setShowRegModal] = useState(false);

  if (!isOpen) return null;

  const percent = total > 0 ? Math.round((correct / total) * 100) : 0;
  const mistakes = total - correct;
  const isLastFree = testNumber === 3;
  const hasMistakes = mistakes > 0;

  const t = (key: string, fallback: string): string =>
    getTranslation(t_nested, `postTestFeedback.${key}`, fallback);

  const bodyTest2 = mistakes === 0
    ? t('bodyTest2Perfect', 'Perfect! Nog één gratis test te gaan, dan start je je persoonlijk examen.')
    : t('bodyTest2Mistakes', `Je maakte ${mistakes} fouten. Nog één test te gaan — dan weet je precies wat je moet verbeteren.`)
        .replace('{mistakes}', String(mistakes));

  const bodyTest3 = t('bodyTest3', `Je maakte ${mistakes} fouten. Dit soort fouten komen vaak terug op het CBR-examen. Blijf oefenen om ze weg te werken.`)
    .replace('{mistakes}', String(mistakes));

  const mistakesStr = String(mistakes);
  const boldMistakesIdx = bodyTest3.indexOf(mistakesStr);

  const handleNextTest = () => {
    const isGuest = userAuth.getCurrentUser()?.isGuest === true;
    if (isGuest) {
      setShowRegModal(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      <RegistrationModal
        isOpen={showRegModal}
        onClose={() => { setShowRegModal(false); onClose(); }}
        onSuccess={() => { setShowRegModal(false); onClose(); navigate('/practice/priority-rules'); }}
      />

      <div className="ptf-overlay" role="dialog" aria-modal="true">
        <div className="ptf-card">
          {/* Progress dots */}
          <div className="ptf-dots">
            <div className="ptf-dot ptf-dot--done" />
            <div className={`ptf-dot ${testNumber >= 2 ? 'ptf-dot--done' : 'ptf-dot--active'}`} />
            <div className={`ptf-dot ${testNumber >= 3 ? 'ptf-dot--done' : ''}`} />
          </div>

          {/* Score ring */}
          <div className="ptf-score-ring" aria-label={`${percent}%`}>
            <svg viewBox="0 0 80 80" className="ptf-score-svg">
              <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
              <circle
                cx="40" cy="40" r="34" fill="none"
                stroke={percent >= 60 ? '#22c55e' : percent >= 40 ? '#ffb81c' : '#ef4444'}
                strokeWidth="7"
                strokeLinecap="round"
                strokeDasharray={`${(2 * Math.PI * 34 * percent) / 100} ${2 * Math.PI * 34}`}
                transform="rotate(-90 40 40)"
              />
            </svg>
            <div className="ptf-score-label">
              <span className="ptf-score-num">{percent}%</span>
              <span className="ptf-score-sub">{correct}/{total}</span>
            </div>
          </div>

          {isLastFree ? (
            <>
              <h2 className="ptf-title">{t('test3Title', 'Je bent dichtbij — maar nog niet klaar')}</h2>
              {hasMistakes && (
                <p className="ptf-warning">{t('warningTest3', 'Met deze fouten is de kans groot dat je nog niet slaagt')}</p>
              )}
              <p className="ptf-body">
                {boldMistakesIdx === -1 ? (
                  bodyTest3
                ) : (
                  <>
                    {bodyTest3.slice(0, boldMistakesIdx)}
                    <strong>{mistakesStr}</strong>
                    {bodyTest3.slice(boldMistakesIdx + mistakesStr.length)}
                  </>
                )}
              </p>
              <button className="ptf-cta ptf-cta--primary" onClick={() => { onClose(); navigate('/subscription'); }}>
                {t('ctaKeepPractising', 'Blijf oefenen — €8/week')}
              </button>
              <button className="ptf-cta ptf-cta--ghost" onClick={onClose}>
                {t('ctaViewResults', 'Resultaten bekijken')}
              </button>
              <p className="ptf-fine">{t('finePrint', 'Stop wanneer je wilt · Goedkoper dan een herexamen')}</p>
            </>
          ) : (
            <>
              <h2 className="ptf-title">{t('test2Title', 'Test 2 klaar 🎯')}</h2>
              {hasMistakes && (
                <p className="ptf-warning">{t('warningTest2', 'Dit soort fouten komen vaak terug op het CBR-examen')}</p>
              )}
              <p className="ptf-body">{bodyTest2}</p>
              <button className="ptf-cta ptf-cta--primary" onClick={handleNextTest}>
                {t('ctaNextTest', 'Door naar test 3')} →
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};
